import React, { useEffect, useState } from "react";
import PageTitle from "../Common/PageTitle";
import { Button, Input, Modal, Radio, Select, Skeleton, Space } from "antd";
import HnProGrpRoundsTable from "./HnProGrpRoundsTable";
import useSWRImmutable from "swr/immutable";
import { fetcher } from "../../services/fectcher";
import {
  deleteFavouriteList,
  getArchivedProviderGrpRecords,
  getProviderGrpRecords,
  postFavouriteList,
} from "../../api/HnProGrpApi";
import useSWR from "swr";
import { FiEdit3 } from "react-icons/fi";
import { BiTrash } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { openNotification } from "../../common/elements/myNotification";

const HNProGrpRoundsHome = () => {
  // Providers API
  const { data: provList } = useSWRImmutable(
    `getccmdproviders?lob=HN`,
    fetcher
  );
  const { data: provGroupList } = useSWRImmutable(
    `getccmdproviderGroup`,
    fetcher
  );
  const { data: cmsProvList } = useSWRImmutable(`getAlignedProvider`, fetcher);
  const { data: cmsProvGroupList } = useSWRImmutable(
    `GetCmsAlignedProvider`,
    fetcher
  );

  // Providers useState
  const [provider, setProvider] = useState(null);
  const [providerGroup, setProviderGroup] = useState(null);
  const [cmsProvider, setCMSProvider] = useState(null);
  const [cmsProviderGroup, setCMSProviderGroup] = useState(null);

  // States for loading and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [freezeEdit, setFreezeEdit] = useState(false);

  // States for storing fetched data
  const [fetchedIndata, setFetchedIndata] = useState(null);
  const [archivedData, setArchivedData] = useState(null);
  const [manageListModalOpen, setManageListModalOpen] = useState(false);

  const [selectedRadioValue, setSelectedRadioValue] = useState(null);
  const [radioData, setRadioData] = useState(null);
  const [editableFavList, setEditableFavList] = useState([]);

  const [isAddingNew, setIsAddingNew] = useState(false);

  // Provider Change Functions
  const onProviderChange = (e) => {
    setProvider(e);
  };

  const onProviderGroupChange = (e) => {
    setProviderGroup(e);
  };

  const onCMSProviderChange = (e) => {
    setCMSProvider(e);
  };

  const onCMSProviderGroupChange = (e) => {
    setCMSProviderGroup(e);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await getProviderGrpRecords({
        provider,
        providerGroup,
        cmsProvider,
        cmsProviderGroup,
      });
      const archiveData = await getArchivedProviderGrpRecords({
        provider,
        providerGroup,
        cmsProvider,
        cmsProviderGroup,
      });
      setFetchedIndata(data);
      setArchivedData(archiveData); // Save the fetched data to state
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data when any of the dependencies change
    if (provider || providerGroup || cmsProvider || cmsProviderGroup) {
      fetchData();
    } else {
      setFetchedIndata(null);
      setArchivedData(null);
    }
  }, [provider, providerGroup, cmsProvider, cmsProviderGroup]);

  const {
    data: favList,
    mutate: favListMutate,
    isLoading: listLoading,
  } = useSWR(
    `getGroupRecords?ManageId=${sessionStorage.getItem("userName")}`,
    fetcher
  );

  const FavouritesList = (favList || [])?.map((item) => ({
    value: item.guid,
    label: item.groupDescription,
  }));

  const handleRadio = async (e) => {
    setFetchedIndata(null);
    const selectedValue = e.target.value;
    setSelectedRadioValue(selectedValue);
    await filterRadio(selectedValue);
  };

  const filterRadio = async (e) => {
    setIsLoading(true);
    try {
      const favouritesData = await fetcher(
        `getGroupPatientRecords?grpGuid=${e}`
      );
      setRadioData(favouritesData);
    } catch (error) {
      console.error("Error fetching or filtering data:", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (favList) {
      setEditableFavList([...favList]);
    }
  }, [favList]);

  const onFavListSave = async () => {
    try {
      setFreezeEdit(true);
      let allSuccessful = true;

      for (const favItem of editableFavList) {
        const body = {
          ...favItem,
          caseManageId: favItem?.caseManageId
            ? favItem?.caseManageId
            : sessionStorage.getItem("userName"),
          createdBy: favItem?.createdBy
            ? favItem?.createdBy
            : sessionStorage.getItem("userName"),
          modifiedBy: favItem?.guid ? sessionStorage.getItem("userName") : "",
        };

        const response = await postFavouriteList(body);

        if (response?.status !== 200) {
          allSuccessful = false;
        }
      }

      if (allSuccessful) {
        openNotification(
          "success",
          "Added",
          "Favourites List Updated Successfully!"
        );
        setManageListModalOpen(false);
        favListMutate();
        setIsAddingNew(false);
      }
      setFreezeEdit(false);
    } catch (errInfo) {
      openNotification("error", "Error", "Favourites List failed to Update");
      console.log("Validation Failed:", errInfo);
    }
    setFreezeEdit(false);
  };

  const handleDeleteFavItem = async (guid) => {
    const flatList = Array.isArray(editableFavList)
      ? editableFavList
      : [editableFavList];

    // Find the specific item using the flattened list
    const body = flatList?.find((item) => item.guid === guid);
    body.modifiedBy = "";

    try {
      setFreezeEdit(true);
      const response = await deleteFavouriteList(body);
      if (response?.status === 200) {
        openNotification("success", "Success", "List Deleted Successfully!");
      }
      favListMutate();
      setFreezeEdit(false);
      setIsAddingNew(false);
    } catch (errInfo) {
      openNotification("error", "Error", "List Failed to Delete");
      console.log("Validation Failed:", errInfo);
    }
    setFreezeEdit(false);
  };

  const handleReset = () => {
    setProvider(null);
    setProviderGroup(null);
    setCMSProvider(null);
    setCMSProviderGroup(null);
    setSelectedRadioValue(null);
    setRadioData(null);
  };

  const onUpdateData = (e) => {
    e === "radio" ? filterRadio(selectedRadioValue) : fetchData();
  };

  const [duplicateError, setDuplicateError] = useState(false);

  useEffect(() => {
    const names = editableFavList.map((item) => item.groupDescription.trim());
    setDuplicateError(new Set(names).size !== names.length);
  }, [editableFavList]);

  return (
    <div>
      <PageTitle title={"HN Provider Group Rounds"} />
      <div className="tw-flex tw-space-x-6 tw-justify-center">
        <div className="tw-w-1/5 tw-flex tw-flex-col border-end tw-pr-4">
          <div className="tw-flex tw-flex-col tw-gap-2 border-bottom tw-pb-4">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-text-zinc-700 tw-font-medium">Filters</div>
              <Button type="text" onClick={handleReset}>
                Reset
              </Button>
            </div>
            <Select
              showSearch
              allowClear
              disabled={
                (providerGroup ||
                cmsProvider ||
                cmsProviderGroup ||
                selectedRadioValue
                  ? true
                  : false) || freezeEdit
              }
              placeholder="Select CCMD Provider..."
              optionFilterProp="children"
              value={provider}
              onChange={onProviderChange}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={provList}
            />
            <Select
              showSearch
              allowClear
              disabled={
                (provider ||
                cmsProvider ||
                cmsProviderGroup ||
                selectedRadioValue
                  ? true
                  : false) || freezeEdit
              }
              placeholder="Select CCMD Provider Group..."
              optionFilterProp="children"
              value={providerGroup}
              onChange={onProviderGroupChange}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={provGroupList}
            />
            <Select
              showSearch
              allowClear
              disabled={
                (provider ||
                providerGroup ||
                cmsProviderGroup ||
                selectedRadioValue
                  ? true
                  : false) || freezeEdit
              }
              placeholder="Select CMS Provider..."
              optionFilterProp="children"
              value={cmsProvider}
              onChange={onCMSProviderChange}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={cmsProvList}
            />
            <Select
              showSearch
              allowClear
              disabled={
                (provider || providerGroup || cmsProvider || selectedRadioValue
                  ? true
                  : false) || freezeEdit
              }
              placeholder="Select CMS Provider Group..."
              optionFilterProp="children"
              value={cmsProviderGroup}
              onChange={onCMSProviderGroupChange}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={cmsProvGroupList}
            />
          </div>
          <div className="tw-pt-4">
            <div className="tw-text-zinc-700 tw-font-medium">Lists</div>

            {listLoading ? (
              <Skeleton active={listLoading} className="tw-mt-10" />
            ) : (
              <>
                <Radio.Group
                  options={FavouritesList}
                  value={selectedRadioValue}
                  onChange={handleRadio}
                  className="tw-flex tw-flex-col tw-gap-3 tw-mt-2 ccmd-checkbox-group"
                  // disabled={
                  //   (provider ||
                  //   providerGroup ||
                  //   cmsProvider ||
                  //   cmsProviderGroup
                  //     ? true
                  //     : false) || freezeEdit
                  // }
                />
                <Button
                  type="default"
                  onClick={() => setManageListModalOpen(true)}
                  className="tw-w-full tw-mt-4"
                >
                  Manage Lists
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="tw-w-4/5">
          {isLoading ? (
            <Skeleton active={isLoading} className="tw-mt-10" />
          ) : (
            <HnProGrpRoundsTable
              mainData={fetchedIndata}
              radioData={radioData}
              archivedData={archivedData}
              onUpdateData={onUpdateData}
              FavouritesList={FavouritesList}
              selectedRadioValue={selectedRadioValue}
            />
          )}
        </div>
      </div>
      {manageListModalOpen && (
        <Modal
          open={manageListModalOpen}
          width={500}
          title={"Manage Lists"}
          onCancel={() => setManageListModalOpen(false)}
          footer={
            <div className="tw-flex tw-flex-col tw-mt-6">
              {duplicateError && (
                <div className="tw-text-red-500 tw-mb-2">
                  Same name exists, please change it.
                </div>
              )}
              <div className="tw-flex tw-justify-between tw-space-x-3">
                <Button
                  onClick={() => {
                    if (!isAddingNew) {
                      setEditableFavList((prevList) => [
                        ...prevList,
                        {
                          key: "",
                          guid: "",
                          groupDescription: "New List",
                          caseManageId: sessionStorage.getItem("userName"),
                          createdBy: sessionStorage.getItem("userName"),
                          modifiedBy: "",
                        },
                      ]);
                      setIsAddingNew(true);
                    }
                  }}
                  disabled={isAddingNew}
                >
                  <FaPlus />
                </Button>
                <Space>
                  <Button
                    type="default"
                    onClick={() => setManageListModalOpen(false)}
                    disabled={freezeEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={onFavListSave}
                    disabled={
                      freezeEdit || !editableFavList.length || duplicateError
                    }
                  >
                    Save
                  </Button>
                </Space>
              </div>
            </div>
          }
        >
          <div className="tw-mt-4">
            {editableFavList.map((list, index) => (
              <div
                key={index}
                className="tw-py-2 tw-flex tw-items-center tw-space-x-4"
              >
                <Input
                  value={list.groupDescription}
                  onChange={(e) => {
                    setEditableFavList((prevList) =>
                      prevList.map((item, i) =>
                        i === index
                          ? { ...item, groupDescription: e.target.value }
                          : item
                      )
                    );
                  }}
                  addonAfter={<FiEdit3 />}
                />
                {list.guid && (
                  <Button onClick={() => handleDeleteFavItem(list.guid)}>
                    <BiTrash className="tw-text-red-500 tw-text-lg" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default HNProGrpRoundsHome;
